import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { REACT_UFO_VERSION } from '../common/constants';
import { getConfig } from '../config';
import { isSegmentLabel, sanitizeUfoName } from '../create-payload/common/utils';
import { getPageVisibilityState } from '../hidden-timing';
function getParentStack(labelStack) {
  if (!labelStack || labelStack.length <= 1) {
    return null;
  }
  return labelStack.slice(0, labelStack.length - 1);
}
function getSegmentId(labelStack) {
  if (!labelStack) {
    return null;
  }
  var leafLabelStack = labelStack[labelStack.length - 1];
  if (isSegmentLabel(leafLabelStack)) {
    return leafLabelStack.segmentId;
  }
  var parentStack = getParentStack(labelStack);
  if (!parentStack) {
    return null;
  }
  return getSegmentId(parentStack);
}
function getParentSegmentId(labelStack) {
  return getSegmentId(getParentStack(labelStack));
}

/**
 * Whenever a render happen, all parent segment have timing reported
 * This method tries to reduce that noise
 */
function removeCascadingParentTimingReport(reactProfilerTimings) {
  var timingIndex = new Map();
  reactProfilerTimings.forEach(function (timing) {
    var segmentId = getSegmentId(timing.labelStack);
    if (segmentId) {
      var _timingIndex$get;
      var timingArray = (_timingIndex$get = timingIndex.get(segmentId)) !== null && _timingIndex$get !== void 0 ? _timingIndex$get : [];
      timingIndex.set(segmentId, timingArray);
      timingArray.push(timing);
    }
  });
  reactProfilerTimings.forEach(function (timing) {
    var parentSegmentId = getParentSegmentId(timing.labelStack);
    if (parentSegmentId) {
      var parentTimings = timingIndex.get(parentSegmentId);
      var filteredParentTimings = parentTimings === null || parentTimings === void 0 ? void 0 : parentTimings.filter(function (parentTiming) {
        if (parentTiming.startTime === timing.startTime && parentTiming.actualDuration === timing.actualDuration) {
          return false;
        }
        return true;
      });
      if (filteredParentTimings) {
        timingIndex.set(parentSegmentId, filteredParentTimings);
      } else {
        timingIndex.delete(parentSegmentId);
      }
    }
  });
  return _toConsumableArray(timingIndex.values()).flatMap(function (v) {
    return v;
  });
}
function transformReactProfilerTimings(reactProfilerTimings) {
  var filtered = removeCascadingParentTimingReport(reactProfilerTimings !== null && reactProfilerTimings !== void 0 ? reactProfilerTimings : []);
  var reactProfilerTimingsMap = filtered.reduce(function (result, _ref) {
    var labelStack = _ref.labelStack,
      startTime = _ref.startTime,
      commitTime = _ref.commitTime,
      actualDuration = _ref.actualDuration,
      type = _ref.type;
    if (labelStack && type !== 'nested-update') {
      var label = labelStack.map(function (ls) {
        return ls.name;
      }).join('/');
      var start = Math.round(startTime);
      var end = Math.round(commitTime);
      var timing = result.get(label) || {
        labelStack: label,
        startTime: start,
        endTime: end,
        mountCount: 0,
        rerenderCount: 1,
        renderDuration: 0
      };
      if (start < timing.startTime) {
        timing.startTime = Math.round(start);
      }
      if (end > timing.endTime) {
        timing.endTime = Math.round(end);
      }
      if (type === 'mount') {
        timing.mountCount += 1;
      }
      if (type === 'update') {
        timing.rerenderCount += 1;
      }
      timing.renderDuration += Math.round(actualDuration);
      result.set(label, timing);
    }
    return result;
  }, new Map());
  return _toConsumableArray(reactProfilerTimingsMap.values());
}
export default function createPostInteractionLogPayload(_ref2) {
  var _window$location;
  var lastInteractionFinish = _ref2.lastInteractionFinish,
    reactProfilerTimings = _ref2.reactProfilerTimings,
    lastInteractionFinishVCResult = _ref2.lastInteractionFinishVCResult,
    postInteractionFinishVCResult = _ref2.postInteractionFinishVCResult;
  var config = getConfig();
  if (!config) {
    throw Error('UFO Configuration not provided');
  }
  var ufoName = sanitizeUfoName(lastInteractionFinish.ufoName);
  var pageVisibilityState = getPageVisibilityState(lastInteractionFinish.start, lastInteractionFinish.end);
  if (pageVisibilityState !== 'visible') {
    return null;
  }
  var maxEndTimeFromProfiler = reactProfilerTimings ? Math.max.apply(Math, _toConsumableArray(reactProfilerTimings.map(function (t) {
    return t.commitTime;
  }))) : lastInteractionFinish.end;
  var revisedEndTime = Math.round(maxEndTimeFromProfiler);
  var revisedTtai = Math.round(maxEndTimeFromProfiler - lastInteractionFinish.start);
  var lastInteractionFinishStart = Math.round(lastInteractionFinish.start);
  var lastInteractionFinishEnd = Math.round(lastInteractionFinish.end);
  var lastInteractionFinishTTAI = Math.round(lastInteractionFinish.end - lastInteractionFinish.start);
  var lastInteractionFinishVC90 = null;
  var lastInteractionFinishVCUpdates = [];
  if (lastInteractionFinishVCResult !== null && lastInteractionFinishVCResult !== void 0 && lastInteractionFinishVCResult['ufo:vc:state']) {
    var _lastInteractionFinis;
    lastInteractionFinishVCUpdates = lastInteractionFinishVCResult === null || lastInteractionFinishVCResult === void 0 ? void 0 : lastInteractionFinishVCResult['ufo:vc:updates'];
    var lastInteractionFinishVCMetrics = lastInteractionFinishVCResult === null || lastInteractionFinishVCResult === void 0 ? void 0 : lastInteractionFinishVCResult['metrics:vc'];
    lastInteractionFinishVC90 = (_lastInteractionFinis = lastInteractionFinishVCMetrics[90]) !== null && _lastInteractionFinis !== void 0 ? _lastInteractionFinis : null;
  }
  var postInteractionFinishVCRatios = {};
  var revisedVC90 = null;
  var postInteractionFinishVCUpdates = [];
  if (postInteractionFinishVCResult !== null && postInteractionFinishVCResult !== void 0 && postInteractionFinishVCResult['ufo:vc:state']) {
    postInteractionFinishVCRatios = postInteractionFinishVCResult === null || postInteractionFinishVCResult === void 0 ? void 0 : postInteractionFinishVCResult['ufo:vc:ratios'];
    postInteractionFinishVCUpdates = postInteractionFinishVCResult === null || postInteractionFinishVCResult === void 0 ? void 0 : postInteractionFinishVCResult['ufo:vc:updates'];
    var postInteractionFinishVCMetrics = postInteractionFinishVCResult === null || postInteractionFinishVCResult === void 0 ? void 0 : postInteractionFinishVCResult['metrics:vc'];
    if (typeof lastInteractionFinishVC90 === 'number') {
      var _postInteractionFinis;
      revisedVC90 = (_postInteractionFinis = postInteractionFinishVCMetrics[90]) !== null && _postInteractionFinis !== void 0 ? _postInteractionFinis : null;
    }
  }
  var lateMutations = postInteractionFinishVCUpdates.filter(function (entry, i) {
    return typeof lastInteractionFinishVCUpdates[i] === 'undefined' || entry.time !== lastInteractionFinishVCUpdates[i].time;
  }).map(function (_ref3) {
    var time = _ref3.time,
      elements = _ref3.elements;
    return {
      time: time,
      elements: Array.from(new Set(elements)).map(function (element) {
        return {
          element: element,
          viewportHeatmapPercentage: postInteractionFinishVCRatios[element]
        };
      })
    };
  });
  return {
    actionSubject: 'experience',
    action: 'measured',
    eventType: 'operational',
    source: 'measured',
    tags: ['observability'],
    attributes: {
      properties: {
        // basic
        'event:hostname': ((_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.hostname) || 'unknown',
        'event:product': config.product,
        'event:schema': '1.0.0',
        'event:source': {
          name: 'react-ufo/web',
          version: REACT_UFO_VERSION,
          payloadSource: 'platform'
        },
        'event:region': config.region || 'unknown',
        'experience:key': 'custom.post-interaction-logs',
        postInteractionLog: {
          lastInteractionFinish: _objectSpread(_objectSpread({}, lastInteractionFinish), {}, {
            ufoName: ufoName,
            start: lastInteractionFinishStart,
            end: lastInteractionFinishEnd,
            ttai: lastInteractionFinishTTAI,
            vc90: lastInteractionFinishVC90
          }),
          revisedEndTime: revisedEndTime,
          revisedTtai: revisedTtai,
          revisedVC90: revisedVC90,
          lateMutations: lateMutations,
          reactProfilerTimings: transformReactProfilerTimings(reactProfilerTimings)
        }
      }
    }
  };
}